// First, add the language options array at the top of the file
import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { setLoader } from 'redux/slices/loader';
import { BsXLg, BsArrowRightShort } from 'react-icons/bs';

import ImageView from 'Components/Image/ImageView';
import { ROUTES } from 'constants/routes';
import { GENERATE_INNER_HTML_PROMPT_POSTFIX } from 'constants';
import ContentGenerationModal from 'Containers/Modal/ContentGenerationModal/ContentGenerationModal';
import { getIsQuestionSubmitButtonEnabled, removeSpacesAndHyphens } from 'helpers';
import { generateStreamResponse } from 'helpers/openAIChat';
import { updateDocument } from 'services/documentService';
import AnswerComponent from './AnswerComponent';
import styles from './Questionnaire.module.scss';
import useCreateDocument from 'hooks/documents/useCreateDocument';
import useQuestions from 'hooks/questions/useQuestions';
import useTemplate from 'hooks/templates/useTemplate';
import BSButton from '../../Components/Button/BSButton';
import * as FiIcons from 'react-icons/fi';
import { IoArrowBack } from 'react-icons/io5';
import ChatHistory from './ChatHistory';
import LiveEditor from './LiveEditor';
import SupportingDocumentsUpload from 'Components/SupportingDocumentsUpload/SupportingDocumentsUpload';
import { extractSupportingDocuments } from 'services/projectService';

// Add language options array
const LANGUAGE_OPTIONS = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'nl', label: 'Dutch' },
  { value: 'ru', label: 'Russian' },
  { value: 'et', label: 'Estonian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'zh-CN', label: 'Chinese (Simplified)' },
  { value: 'zh-TW', label: 'Chinese (Traditional)' },
  { value: 'ar', label: 'Arabic' },
  { value: 'bg', label: 'Bulgarian' },
  { value: 'ca', label: 'Catalan' },
  { value: 'hr', label: 'Croatian' },
  { value: 'cs', label: 'Czech' },
  { value: 'da', label: 'Danish' },
  { value: 'fi', label: 'Finnish' },
  { value: 'el', label: 'Greek' },
  { value: 'he', label: 'Hebrew' },
  { value: 'hi', label: 'Hindi' },
  { value: 'hu', label: 'Hungarian' },
  { value: 'id', label: 'Indonesian' },
  { value: 'ko', label: 'Korean' },
  { value: 'lv', label: 'Latvian' },
  { value: 'lt', label: 'Lithuanian' },
  { value: 'ms', label: 'Malay' },
  { value: 'no', label: 'Norwegian' },
  { value: 'fa', label: 'Persian' },
  { value: 'pl', label: 'Polish' },
  { value: 'ro', label: 'Romanian' },
  { value: 'sr', label: 'Serbian' },
  { value: 'sk', label: 'Slovak' },
  { value: 'sl', label: 'Slovenian' },
  { value: 'sv', label: 'Swedish' },
  { value: 'th', label: 'Thai' },
  { value: 'tr', label: 'Turkish' },
  { value: 'uk', label: 'Ukrainian' },
  { value: 'ur', label: 'Urdu' },
  { value: 'vi', label: 'Vietnamese' },
];

const Questionnaire = ({
  SideBarView,
  templateIdParams,
  documentTemplateOptions = [],
  refetchContent = async () => {},
}) => {
  const [questionsState, setQuestionsState] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [streamContent, setStreamContent] = useState('');
  const [contentGenerated, setContentGenerated] = useState(false);
  const [templateOptions, setTemplateOptions] = useState([]);
  // Add state for selected language, default to English US
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');

  // Split screen mode states (separate from main modal states)
  const [splitScreenMode, setSplitScreenMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]); // Array of chat messages
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentActivity, setCurrentActivity] = useState(null); // Activity indicator
  const [currentEditablePrompt, setCurrentEditablePrompt] = useState(''); // For initial prompt before split screen

  // Artifact states
  const [artifactVersions, setArtifactVersions] = useState([]); // Array of artifact versions
  const [currentArtifactVersion, setCurrentArtifactVersion] = useState(0); // Current version index
  const [splitStreamContent, setSplitStreamContent] = useState('');
  const [splitContentGenerated, setSplitContentGenerated] = useState(false);

  // Editable prompt states for regular questionnaire view
  const [isEditingMainPrompt, setIsEditingMainPrompt] = useState(false);
  const [editablePromptText, setEditablePromptText] = useState('');
  const [editablePromptHtml, setEditablePromptHtml] = useState(''); // HTML version with highlights

  // Supporting documents upload states
  const [supportingFiles, setSupportingFiles] = useState([]);
  const [supportingContent, setSupportingContent] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [uploadErrors, setUploadErrors] = useState([]);

  const { user } = useSelector((state) => state.auth);

  const { mutateAsync: createDocument } = useCreateDocument({
    userId: user?.id,
  });

  const abortController = useRef();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { templateId, documentId } = useParams();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  const taskId = searchParams?.get('taskId');
  const projectId = searchParams?.get('projectId');
  const folderId = searchParams?.get('folderId');

  const transformedTemplateId = templateIdParams || templateId;

  const { questions: resQuestions } = useQuestions(transformedTemplateId);
  const { template } = useTemplate(transformedTemplateId);

  const {
    title: templateName,
    prompt: templatePrompt,
  } = template || {};

  // Initialize editable prompt text when template prompt changes
  useEffect(() => {
    if (templatePrompt) {
      // Just use the raw template prompt for editing (without HTML rendering)
      setEditablePromptText(templatePrompt);
    }
  }, [templatePrompt]);

  // Default brand color (teal) for consistent styling
  const brandColor = '#00a7b7';

  const initialQuestions = useMemo(
    () =>
      resQuestions?.[0].inputs.map((question, index) => {
        return {
          ...question,
          id: question.id || index + 1,
        };
      }),
    [resQuestions],
  );

  const initialQuestionsState = useMemo(
    () =>
      resQuestions?.[0].inputs
        .sort((a, b) => a.position - b.position)
        .map((question, index) => {
          const match = documentTemplateOptions.find(
            (d) => d.title === question.title,
          );
          return {
            ...question,
            id: question.id || index + 1,
            title: question.title || index + 1,
            selectedOption: match?.value ?? '',
            inputValue: match?.value ?? '',
            position: question.position,
          };
        }) || [],
    [resQuestions],
  );

  useEffect(() => {
    setQuestions(initialQuestions);
    setQuestionsState(initialQuestionsState);
  }, [resQuestions]);

  // Apply brand color as CSS custom property
  useEffect(() => {
    if (brandColor) {
      document.documentElement.style.setProperty('--template-brand-color', brandColor);
      // Create lighter version for backgrounds (10% opacity)
      const rgb = brandColor.match(/\w\w/g)?.map((x) => parseInt(x, 16));
      if (rgb) {
        document.documentElement.style.setProperty(
          '--template-brand-color-light',
          `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.1)`
        );
        document.documentElement.style.setProperty(
          '--template-brand-color-bg',
          `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.05)`
        );
      }
    }
    return () => {
      document.documentElement.style.removeProperty('--template-brand-color');
      document.documentElement.style.removeProperty('--template-brand-color-light');
      document.documentElement.style.removeProperty('--template-brand-color-bg');
    };
  }, [brandColor]);

  // Apply dynamic dropdown arrow color
  useEffect(() => {
    if (brandColor) {
      const dropdown = document.querySelector(`.${styles.language_dropdown}`);
      if (dropdown) {
        // Convert hex to URL-safe format for SVG
        const colorHex = brandColor.replace('#', '%23');
        const svgIcon = `data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='${colorHex}' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E`;
        dropdown.style.backgroundImage = `url("${svgIcon}")`;
      }
    }
  }, [brandColor]);

  // Watch for content generation completion in split screen mode
  useEffect(() => {
    if (splitContentGenerated && isGenerating && splitStreamContent) {
      // Store new artifact version
      const newVersion = {
        id: Date.now(),
        version: artifactVersions.length + 1,
        content: splitStreamContent,
        timestamp: new Date(),
      };
      setArtifactVersions(prev => [...prev, newVersion]);
      setCurrentArtifactVersion(artifactVersions.length);

      // Clear activity indicator
      setCurrentActivity(null);
      setIsGenerating(false);
    }
  }, [splitContentGenerated, splitStreamContent, isGenerating, artifactVersions.length]);

  const handleChange = (index, type, option, action) => {
    const transformedQuestions = questionsState.map((question, qIndex) => {
      if (qIndex === index) {
        const { selectedOption, title } = question || {};

        // Allow multiple selections for Tone and Style fields
        const allowMultiple = title === 'Tone' || title === 'Style';

        const selectedOptions =
          action === 'add'
            ? type === 'radio' && !allowMultiple
              ? [option]
              : option
                ? [...selectedOption, option]
                : selectedOption
            : selectedOption.filter((val) => val !== option);

        return {
          ...question,
          selectedOption: selectedOptions,
          inputValue: '',
        };
      }
      return question;
    });

    setQuestionsState(transformedQuestions);
  };

  const handleInputChange = (value, index, isTagsInput) => {
    const transformedQuestions = questionsState.map((question, qIndex) => {
      if (qIndex === index) {
        return {
          ...question,
          inputValue: isTagsInput ? value.join(',') : value,
          selectedOption: [],
        };
      }
      return question;
    });

    setQuestionsState(transformedQuestions);
  };

  // Add a handler for language selection
  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
  };

  // Handler for supporting documents upload
  const handleSupportingFilesChange = async (files) => {
    setSupportingFiles(files);
    setUploadErrors([]);

    if (files.length > 0) {
      setIsExtracting(true);
      setExtractionProgress(0);

      // Simulate progress while extracting
      const progressInterval = setInterval(() => {
        setExtractionProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      try {
        const response = await extractSupportingDocuments(files);
        clearInterval(progressInterval);
        setExtractionProgress(100);
        setSupportingContent(response?.data?.data || '');
      } catch (error) {
        clearInterval(progressInterval);
        setUploadErrors(['Failed to extract content from files. Please try again.']);
        setSupportingContent('');
      } finally {
        setIsExtracting(false);
      }
    } else {
      setSupportingContent('');
    }
  };

  const handleContentGenerated = async () => {
    if (SideBarView) {
      await updateDocument(documentId, { 
        content: streamContent,
        language: selectedLanguage // Add language to document update
      });
      setOpenModal(false);
      await refetchContent();
    } else {
      await createDocument(
        {
          document: {
            title: questionsState[0]?.inputValue,
            user_id: user.id,
            template_id: transformedTemplateId,
            content: streamContent,
            template_options: templateOptions,
            language: selectedLanguage // Add language to document creation
          },
          taskId,
          projectId,
          folderId,
        },
        {
          onSuccess: (documentResponse) => {
            const documentId = documentResponse?.data?.document?.id;
            if (!documentId) return;

            const routeToMove = ROUTES.EditorDocumentTemplate(
              documentId,
              transformedTemplateId,
            );
            navigate(routeToMove);
          },
        },
      );
    }
  };

  useEffect(() => {
    if (contentGenerated) {
      handleContentGenerated();
    }
  }, [contentGenerated]);

  const handleBlockSave = async () => {
    let transformedMessage = templatePrompt;
    const templateOptionsTemp = [];

    questionsState.forEach(
      ({
        title,
        input_type: inputType,
        inputValue,
        selectedOption,
        id: questionId,
        multiple,
      }) => {
        let value = '';
        if (inputType === 'text' || inputType === 'textarea' || inputType === 'number' || inputType === 'email' || inputType === 'tags') {
          value = inputValue;
        } else if (inputType === 'radio') {
          value = selectedOption[0] || '';
        } else if (inputType === 'checkbox') {
          value = selectedOption && selectedOption.length > 0 ? selectedOption.join(', ') : '';
        } else {
          value = inputValue;
        }

        const allChild = questionsState
          .filter((question) => question.id === questionId && multiple)
          ?.map((question) => question.inputValue);

        transformedMessage = transformedMessage.replaceAll(
          `[${title.toUpperCase()}]`,
          `${value}`,
        );

        templateOptionsTemp.push({ title, value });

        if (allChild?.length) {
          const content = allChild
            .map((_, index) => {
              return `
            [Product: ${index + 1}]
            [Summary]
            [Description]
            [About this Item]
            [Reviews]
            [Pros & Cons]
            [Why should I buy this product]`;
            })
            .join('\n\n');
          transformedMessage = transformedMessage.replaceAll(
            `[Product]`,
            allChild,
          );

          transformedMessage = transformedMessage.replaceAll(
            `[5 Products Types]`,
            `[${allChild.length} Products Types]`,
          );

          transformedMessage = transformedMessage.replaceAll(
            `5 products`,
            `${allChild.length} products`,
          );

          transformedMessage = transformedMessage.replaceAll(
            `5 detailed product`,
            `${allChild.length} detailed product`,
          );

          transformedMessage = transformedMessage.replaceAll(
            `[Product: 1]\n[Summary]\n[Description]\n[About this Item]\n[Reviews]\n[Pros & Cons]\n[Why should I buy this product]`,
            content,
          );
        }
      },
    );

    // Add language instruction to the prompt
    const selectedLanguageOption = LANGUAGE_OPTIONS.find(option => option.value === selectedLanguage);
    const languageLabel = selectedLanguageOption ? selectedLanguageOption.label : 'English (US)';

    // Add appropriate language instruction based on the selected language
    if (selectedLanguage !== 'en-US' && selectedLanguage !== 'en-GB') {
      transformedMessage += `\n\nPlease ensure the entire output is in ${languageLabel}.`;
    } else if (selectedLanguage === 'en-GB') {
      transformedMessage += `\n\nPlease ensure the entire output uses British English spelling and terminology.`;
    }

    // Add supporting documents context if available
    if (supportingContent) {
      transformedMessage += `\n\n📄 SUPPORTING DOCUMENTS CONTEXT:\nThe user has provided the following reference documents to guide content creation. Use these documents to ensure accuracy, align with the brand's messaging, and create more relevant and customized content:\n\n${supportingContent}\n\n=== END OF SUPPORTING DOCUMENTS ===`;
    }

    abortController.current = new AbortController();
    setTemplateOptions({ options: templateOptionsTemp });

    // Enable split screen mode on first run
    if (!splitScreenMode) {
      setSplitScreenMode(true);
      // Use HTML-highlighted version for Live Prompt display
      setCurrentEditablePrompt(renderLivePrompt(templatePrompt));
      // Don't add initial message - Live Prompt serves as first interaction
    }

    // Show activity indicator
    const activityType = artifactVersions.length === 0 ? 'creating' : 'editing';
    const nextVersion = artifactVersions.length + 1;
    setCurrentActivity({
      activityType,
      version: nextVersion,
    });

    // Use split screen states instead of main modal states
    setSplitStreamContent('');
    setSplitContentGenerated(false);
    setIsGenerating(true);

    const URL = `${process.env.REACT_APP_URL}/api/v1/completion_stream_data`;
    const BODY = JSON.stringify({
      prompt: `${transformedMessage}\n${GENERATE_INNER_HTML_PROMPT_POSTFIX}`,
      ai_model: 'claude-sonnet-4-6',
    });

    try {
      await generateStreamResponse(
        URL,
        BODY,
        setSplitStreamContent,
        setSplitContentGenerated,
        abortController?.current.signal,
      );
    } catch (error) {
      dispatch(
        setLoader({
          isLoading: false,
        }),
      );
      setIsGenerating(false);
      setCurrentActivity(null);
    }
  };

  const handleMultipleQuestionAdd = (questionId, index) => {
    const parentQuestion = questions.find(
      (question) => question.id === questionId,
    );

    const previousChildQuestionsCount = questions.filter(
      (question) => question.id === questionId,
    ).length;

    const childQuestion = {
      ...parentQuestion,
      childId: `${questionId}_${previousChildQuestionsCount - 1}`,
      position: parentQuestion.position + previousChildQuestionsCount,
    };

    const transformedQuestions = questions.reduce((acc, question, qIndex) => {
      if (qIndex === index) {
        acc.push(...[question, childQuestion]);
        return acc;
      }

      if (qIndex > index) {
        acc.push({
          ...question,
          position: question.position + 1,
        });
        return acc;
      }

      acc.push(question);
      return acc;
    }, []);

    const transformedQuestionState = questionsState.reduce(
      (acc, question, qIndex) => {
        if (qIndex === index) {
          acc.push(...[question, childQuestion]);
          return acc;
        }

        if (qIndex > index) {
          acc.push({
            ...question,
            position: question.position + 1,
          });
          return acc;
        }

        acc.push(question);
        return acc;
      },
      [],
    );

    setQuestions(transformedQuestions);
    setQuestionsState(transformedQuestionState);
  };

  const handleRemoveChildQuestion = (index) => {
    const transformedQuestions = questions.reduce((acc, question, qIndex) => {
      if (qIndex === index) {
        return acc;
      }

      if (qIndex > index) {
        acc.push({
          ...question,
          position: question.position - 1,
        });
        return acc;
      }

      acc.push(question);
      return acc;
    }, []);

    const transformedQuestionsState = questionsState.reduce(
      (acc, question, qIndex) => {
        if (qIndex === index) {
          return acc;
        }

        if (qIndex > index) {
          acc.push({
            ...question,
            position: question.position - 1,
          });
          return acc;
        }

        acc.push(question);
        return acc;
      },
      [],
    );

    setQuestionsState(transformedQuestionsState);
    setQuestions(transformedQuestions);
  };

  const sortedQuestions =
    questions?.sort((a, b) => a.position - b.position) || [];

  const { isSubmitButtonEnabled, errors } =
    getIsQuestionSubmitButtonEnabled(questionsState);

  // Helper function to replace variables with actual user input in real-time
  const renderLivePrompt = (prompt) => {
    if (!prompt) return '';
    if (!questionsState || questionsState.length === 0) return prompt;

    let transformedPrompt = prompt;

    // Get RGB values for gradient background
    const rgb = brandColor.match(/\w\w/g)?.map((x) => parseInt(x, 16));
    const lightBg = rgb ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.1)` : '#f2fbfb';
    const lighterBg = rgb ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.05)` : '#ffffff';

    // Replace each variable with actual user input
    questionsState.forEach(({ title, inputValue, selectedOption, input_type: inputType }) => {
      let value = '';

      if (inputType === 'text' || inputType === 'textarea' || inputType === 'number' || inputType === 'email' || inputType === 'tags') {
        value = inputValue;
      } else if (inputType === 'radio') {
        // Allow multiple selections for Tone and Style, join with commas
        const allowMultiple = title === 'Tone' || title === 'Style';
        value = allowMultiple && selectedOption && selectedOption.length > 0
          ? selectedOption.join(', ')
          : selectedOption[0] || '';
      } else if (inputType === 'checkbox') {
        // Join multiple checkbox selections with commas
        value = selectedOption && selectedOption.length > 0 ? selectedOption.join(', ') : '';
      } else {
        value = inputValue;
      }

      const variablePattern = `[${title.toUpperCase()}]`;
      const variablePatternUnderscore = `[${title.toUpperCase().replace(/ /g, '_')}]`;

      if (value && value.trim()) {
        // If there's a value, replace with highlighted filled value using template brand color
        const highlightedValue = `<span style="background: linear-gradient(135deg, ${lightBg} 0%, ${lighterBg} 100%); color: ${brandColor}; font-weight: 600; padding: 2px 6px; border-radius: 4px;">${value}</span>`;
        transformedPrompt = transformedPrompt.replaceAll(variablePattern, highlightedValue);
        transformedPrompt = transformedPrompt.replaceAll(variablePatternUnderscore, highlightedValue);
      } else {
        // If no value yet, show the variable placeholder in brand color
        const highlightedPlaceholder = `<span style="color: ${brandColor}; font-weight: 500;">${variablePattern}</span>`;
        transformedPrompt = transformedPrompt.replaceAll(variablePattern, highlightedPlaceholder);
        transformedPrompt = transformedPrompt.replaceAll(variablePatternUnderscore, highlightedPlaceholder);
      }
    });

    return transformedPrompt;
  };

  // Handler for re-running prompt from chat history
  const handleRerunPrompt = async () => {
    if (!currentEditablePrompt.trim() || isGenerating) return;

    abortController.current = new AbortController();
    setSplitStreamContent('');
    setSplitContentGenerated(false);
    setIsGenerating(true);

    const URL = `${process.env.REACT_APP_URL}/api/v1/completion_stream_data`;
    const BODY = JSON.stringify({
      prompt: `${currentEditablePrompt}\n${GENERATE_INNER_HTML_PROMPT_POSTFIX}`,
      ai_model: 'claude-sonnet-4-6',
    });

    try {
      await generateStreamResponse(
        URL,
        BODY,
        setSplitStreamContent,
        setSplitContentGenerated,
        abortController?.current.signal,
      );
    } catch (error) {
      dispatch(
        setLoader({
          isLoading: false,
        }),
      );
      setIsGenerating(false);
    }
  };

  // Handler for manual content edits
  const handleContentChange = (newContent) => {
    setSplitStreamContent(newContent);
  };

  // Extract title from content (first H1 heading) - Helper function
  const extractTitleFromContent = (htmlContent) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    // Try to find the first H1
    const h1 = tempDiv.querySelector('h1');
    if (h1 && h1.textContent.trim()) {
      return h1.textContent.trim();
    }

    // Fall back to template name if no H1 found
    return templateName;
  };

  // Handler for saving content to document
  const handleSaveToDocument = async () => {
    if (!splitStreamContent) return;

    const taskId = searchParams?.get('taskId');
    const projectId = searchParams?.get('projectId');
    const documentTitle = extractTitleFromContent(splitStreamContent);

    try {
      const documentResponse = await createDocument({
        document: {
          title: documentTitle,
          content: splitStreamContent,
          template_options: templateOptions,
          language: selectedLanguage,
        },
        taskId,
        projectId,
        folderId,
      });

      // Show success message and stay in split screen
      const documentId = documentResponse?.data?.document?.id;
      if (documentId) {
        alert('Content saved to document successfully! You can find it in your Library.');
        // Stay in split screen - no navigation
      }
    } catch (error) {
      alert('Failed to save document. Please try again.');
    }
  };

  // Handler for reviewing content in the main editor
  const handleReviewInEditor = async () => {
    if (!splitStreamContent) return;

    const taskId = searchParams?.get('taskId');
    const projectId = searchParams?.get('projectId');
    const documentTitle = extractTitleFromContent(splitStreamContent);

    try {
      const documentResponse = await createDocument({
        document: {
          title: documentTitle,
          content: splitStreamContent,
          template_options: templateOptions,
          language: selectedLanguage,
        },
        taskId,
        projectId,
        folderId,
      });

      const documentId = documentResponse?.data?.document?.id;
      if (documentId) {
        // Navigate to the editor
        const routeToMove = ROUTES.EditorDocumentTemplate(
          documentId,
          transformedTemplateId,
        );
        navigate(routeToMove);
      }
    } catch (error) {
      alert('Failed to open in editor. Please try again.');
    }
  };

  // Handler for editing the initial prompt
  const handleEditPrompt = () => {
    // Exit split screen mode to return to questionnaire
    setSplitScreenMode(false);
    // Clear split screen states
    setChatMessages([]);
    setSplitStreamContent('');
    setSplitContentGenerated(false);
    setArtifactVersions([]);
    setCurrentArtifactVersion(0);
    setCurrentActivity(null);
  };

  // Handler for regenerating with edited prompt (inline editing)
  const handleRegeneratePrompt = async (editedPromptText) => {
    if (!editedPromptText.trim() || isGenerating) return;

    // Update the current editable prompt
    setCurrentEditablePrompt(editedPromptText);

    // Show activity indicator
    const activityType = artifactVersions.length === 0 ? 'creating' : 'editing';
    const nextVersion = artifactVersions.length + 1;
    setCurrentActivity({
      activityType,
      version: nextVersion,
    });

    // Start generation
    setSplitStreamContent('');
    setSplitContentGenerated(false);
    setIsGenerating(true);

    const URL = `${process.env.REACT_APP_URL}/api/v1/completion_stream_data`;
    const BODY = JSON.stringify({
      prompt: `${editedPromptText}\n${GENERATE_INNER_HTML_PROMPT_POSTFIX}`,
      ai_model: 'claude-sonnet-4-6',
    });

    try {
      abortController.current = new AbortController();
      await generateStreamResponse(
        URL,
        BODY,
        setSplitStreamContent,
        setSplitContentGenerated,
        abortController?.current.signal,
      );
    } catch (error) {
      dispatch(
        setLoader({
          isLoading: false,
        }),
      );
      setIsGenerating(false);
      setCurrentActivity(null);
    }
  };

  // Handler for sending chat messages (Phase 1-3)
  const handleSendMessage = async (userMessage) => {
    if (!userMessage.trim() || isGenerating) return;

    // Add user message to chat
    const userMsg = {
      id: Date.now(),
      type: 'user',
      content: userMessage,
      timestamp: new Date(),
    };
    setChatMessages(prev => [...prev, userMsg]);

    // Show activity indicator
    const activityType = artifactVersions.length === 0 ? 'creating' : 'editing';
    const nextVersion = artifactVersions.length + 1;
    setCurrentActivity({
      activityType,
      version: nextVersion,
    });

    // Start generation
    setSplitStreamContent('');
    setSplitContentGenerated(false);
    setIsGenerating(true);

    // Build prompt with context if editing existing artifact
    let fullPrompt = userMessage;
    if (artifactVersions.length > 0 && splitStreamContent) {
      // Detect if it's a small, surgical edit
      const isSmallEdit = userMessage.length < 150 && (
        /\b(change|fix|update|replace|add|remove|make|set|bold|italic|color|font|title|heading|conclusion|intro|cta|button|link)\b/i.test(userMessage)
      );

      if (isSmallEdit) {
        // Surgical edit prompt - emphasizes minimal changes
        fullPrompt = `Current content:\n${splitStreamContent}\n\nTask: ${userMessage}\n\nCRITICAL INSTRUCTION: Make ONLY the specific change requested above. Keep all other content EXACTLY as-is, word-for-word. Do not improve, rephrase, rewrite, or modify anything else. Copy unchanged sections verbatim.`;
      } else {
        // Broader edit prompt - allows more extensive changes
        fullPrompt = `Current content:\n${splitStreamContent}\n\nUser request: ${userMessage}\n\nPlease make the requested changes to the content above. Only output the updated content, not explanations.`;
      }
    }

    const URL = `${process.env.REACT_APP_URL}/api/v1/completion_stream_data`;
    const BODY = JSON.stringify({
      prompt: `${fullPrompt}\n${GENERATE_INNER_HTML_PROMPT_POSTFIX}`,
      ai_model: 'claude-sonnet-4-6',
    });

    try {
      abortController.current = new AbortController();
      await generateStreamResponse(
        URL,
        BODY,
        setSplitStreamContent,
        setSplitContentGenerated,
        abortController?.current.signal,
      );

      // After streaming completes, add AI response to chat
      // Note: The useEffect will handle version tracking
    } catch (error) {
      dispatch(
        setLoader({
          isLoading: false,
        }),
      );
      setIsGenerating(false);
      setCurrentActivity(null);
    }
  };

  // Handler for main prompt click to edit
  const handleMainPromptClick = () => {
    // Build transformed message inline (same approach as handleBlockSave)
    let transformedMessage = templatePrompt;

    questionsState.forEach(
      ({
        title,
        input_type: inputType,
        inputValue,
        selectedOption,
      }) => {
        let value = '';
        if (inputType === 'text' || inputType === 'textarea' || inputType === 'number' || inputType === 'email' || inputType === 'tags') {
          value = inputValue;
        } else if (inputType === 'radio') {
          value = selectedOption[0] || '';
        } else if (inputType === 'checkbox') {
          value = selectedOption && selectedOption.length > 0 ? selectedOption.join(', ') : '';
        } else {
          value = inputValue;
        }

        transformedMessage = transformedMessage.replaceAll(
          `[${title.toUpperCase()}]`,
          `${value}`,
        );
      },
    );

    setEditablePromptText(transformedMessage);
    setEditablePromptHtml(renderLivePrompt(templatePrompt)); // Store HTML version with highlights
    setIsEditingMainPrompt(true);
  };

  // Handler for saving edited main prompt
  const handleSaveMainPrompt = () => {
    setIsEditingMainPrompt(false);
    // The edited text is already in editablePromptText state
  };

  // Handler for canceling main prompt edit
  const handleCancelMainPrompt = () => {
    setIsEditingMainPrompt(false);
    // Reset to original template prompt
    if (templatePrompt) {
      setEditablePromptText(templatePrompt);
    }
  };

  // Handler for running custom prompt from main view
  const handleRunCustomPrompt = async () => {
    if (!editablePromptText.trim()) return;

    // Add language instruction if needed
    const selectedLanguageOption = LANGUAGE_OPTIONS.find(option => option.value === selectedLanguage);
    const languageLabel = selectedLanguageOption ? selectedLanguageOption.label : 'English (US)';

    let promptWithLanguage = editablePromptText;
    if (selectedLanguage !== 'en-US' && selectedLanguage !== 'en-GB') {
      promptWithLanguage += `\n\nPlease ensure the entire output is in ${languageLabel}.`;
    } else if (selectedLanguage === 'en-GB') {
      promptWithLanguage += `\n\nPlease ensure the entire output uses British English spelling and terminology.`;
    }

    // Add supporting documents context if available
    if (supportingContent) {
      promptWithLanguage += `\n\n📄 SUPPORTING DOCUMENTS CONTEXT:\nThe user has provided the following reference documents to guide content creation. Use these documents to ensure accuracy, align with the brand's messaging, and create more relevant and customized content:\n\n${supportingContent}\n\n=== END OF SUPPORTING DOCUMENTS ===`;
    }

    abortController.current = new AbortController();

    // Enable split screen mode (same as handleBlockSave)
    if (!splitScreenMode) {
      setSplitScreenMode(true);
      // Use the HTML version with highlights for Live Prompt display
      setCurrentEditablePrompt(editablePromptHtml || renderLivePrompt(templatePrompt));
    }

    // Exit edit mode
    setIsEditingMainPrompt(false);

    // Show activity indicator
    const activityType = artifactVersions.length === 0 ? 'creating' : 'editing';
    const nextVersion = artifactVersions.length + 1;
    setCurrentActivity({
      activityType,
      version: nextVersion,
    });

    // Use split screen states
    setSplitStreamContent('');
    setSplitContentGenerated(false);
    setIsGenerating(true);

    const URL = `${process.env.REACT_APP_URL}/api/v1/completion_stream_data`;
    const BODY = JSON.stringify({
      prompt: `${promptWithLanguage}\n${GENERATE_INNER_HTML_PROMPT_POSTFIX}`,
      ai_model: 'claude-sonnet-4-6',
    });

    try {
      await generateStreamResponse(
        URL,
        BODY,
        setSplitStreamContent,
        setSplitContentGenerated,
        abortController?.current.signal,
      );
    } catch (error) {
      dispatch(
        setLoader({
          isLoading: false,
        }),
      );
      setIsGenerating(false);
      setCurrentActivity(null);
    }
  };

  return (
    <>
      {!SideBarView && templateName && (
        <section className={styles.header_container}>
          <Link to={ROUTES.Templates}>
            <IoArrowBack color="#101828" size={16} />
          </Link>
          <div className={styles.header_container__icon}>
            <ImageView
              src={
                templateName?.toLowerCase().includes('x thread')
                  ? '/Images/x.svg'
                  : templateName?.toLowerCase().includes('viral tweet')
                    ? '/Images/x.svg'
                    : templateName?.toLowerCase().includes('carousel')
                      ? '/Images/InstagramPosts.svg'
                      : templateName?.toLowerCase().includes('email sequence')
                        ? '/Images/Email.svg'
                        : templateName?.toLowerCase().includes('reel')
                          ? '/Images/InstagramPosts.svg'
                          : templateName?.toLowerCase().includes('linkedin')
                            ? '/Images/LinkedInLogo.svg'
                            : templateName?.toLowerCase().includes('youtube')
                              ? '/Images/YouTubeScript.svg'
                              : templateName?.toLowerCase().includes('tiktok')
                                ? '/Images/TikTokScript.svg'
                                : templateName?.toLowerCase().includes('webinar')
                                  ? '/Images/WebinarScript.svg'
                                  : `/Images/${removeSpacesAndHyphens(templateName)}.svg`
              }
              style={{
                width: (templateName?.toLowerCase().includes('x thread') || templateName?.toLowerCase().includes('viral tweet')) ? '20px' : '32px',
                height: (templateName?.toLowerCase().includes('x thread') || templateName?.toLowerCase().includes('viral tweet')) ? '20px' : '32px'
              }}
            />
          </div>
          <h2 className={styles.header_container__heading}>{templateName}</h2>
        </section>
      )}

      {/* Split Screen Mode */}
      {splitScreenMode ? (
        <div className={styles.split_screen_container}>
          <div className={styles.split_screen_left}>
            <ChatHistory
              messages={chatMessages}
              onSendMessage={handleSendMessage}
              isGenerating={isGenerating}
              currentActivity={currentActivity}
              initialPrompt={currentEditablePrompt}
              onEditPrompt={handleEditPrompt}
              onRegeneratePrompt={handleRegeneratePrompt}
            />
          </div>
          <div className={styles.split_screen_right}>
            <LiveEditor
              streamContent={
                isGenerating
                  ? splitStreamContent
                  : artifactVersions[currentArtifactVersion]?.content || splitStreamContent
              }
              isGenerating={isGenerating}
              contentGenerated={splitContentGenerated}
              onSaveToDocument={handleSaveToDocument}
              onReviewInEditor={handleReviewInEditor}
              onContentChange={(newContent) => {
                // Update the current version's content when edited
                if (artifactVersions.length > 0) {
                  setArtifactVersions(prev => prev.map((v, i) =>
                    i === currentArtifactVersion ? { ...v, content: newContent } : v
                  ));
                }
                handleContentChange(newContent);
              }}
              currentVersion={currentArtifactVersion + 1}
              totalVersions={artifactVersions.length}
              onVersionChange={(newVersion) => {
                // newVersion is 1-indexed from UI, convert to 0-indexed
                const index = newVersion - 1;
                if (index >= 0 && index < artifactVersions.length) {
                  setCurrentArtifactVersion(index);
                }
              }}
            />
          </div>
        </div>
      ) : (
        <>
        <div className={`${styles.Questionaire} ${SideBarView ? 'pt-0' : ''}`}>
        <section
          className={'d-flex flex-column ' + styles.Questionaire__Section}
        >
          {/* Prompt Display Section */}
          {!SideBarView && templatePrompt && (
            <div className={styles.prompt_card}>
              <div className={styles.prompt_header}>
                <h4 className={styles.prompt_title}>Live Prompt</h4>
                <p className={styles.prompt_subtitle}>
                  {isEditingMainPrompt
                    ? 'Edit your prompt and click Save or Cancel'
                    : 'Fill in the fields below and watch your prompt update in real-time. Click to edit the prompt directly.'}
                </p>
              </div>
              <div className={styles.prompt_display}>
                {isEditingMainPrompt ? (
                  <>
                    <div
                      className={styles.prompt_textarea}
                      contentEditable
                      onInput={(e) => {
                        setEditablePromptText(e.currentTarget.textContent || '');
                        setEditablePromptHtml(e.currentTarget.innerHTML || '');
                      }}
                      dangerouslySetInnerHTML={{ __html: renderLivePrompt(templatePrompt) }}
                      style={{ minHeight: '200px', whiteSpace: 'pre-wrap' }}
                    />
                    <div className={styles.prompt_actions}>
                      <button
                        className={styles.prompt_action_cancel}
                        onClick={handleCancelMainPrompt}
                      >
                        Cancel
                      </button>
                      <button
                        className={styles.prompt_action_save}
                        onClick={handleSaveMainPrompt}
                      >
                        Save
                      </button>
                      <button
                        className={styles.prompt_action_run}
                        onClick={handleRunCustomPrompt}
                        disabled={!editablePromptText.trim()}
                      >
                        <FiIcons.FiPlay style={{ marginRight: '6px' }} />
                        Run Custom Prompt
                      </button>
                    </div>
                  </>
                ) : (
                  <div
                    className={styles.prompt_text}
                    onClick={handleMainPromptClick}
                    style={{ cursor: 'pointer' }}
                    dangerouslySetInnerHTML={{
                      __html: renderLivePrompt(templatePrompt)
                    }}
                  />
                )}
              </div>
            </div>
          )}

          <div
            className={`${styles.Questionaire_InnerWrapper} ${
              SideBarView ? 'px-2 py-2 shadow-none' : 'py-3'
            }`}
          >
            <section
              className={`d-flex ${styles.Question} ${
                SideBarView && 'flex-column gap-0'
              }`}
            >
              <div className="w-100">
                {!SideBarView && (
                  <>
                    <h2 className={styles.Question_questionText}>
                      Your {templateName} is almost ready
                    </h2>
                    <span className={styles.Question_subStr}>
                      Just a few inputs away from publish-ready content
                    </span>
                  </>
                )}
                <div className={`${styles.Question_Form_Wrapper} ${SideBarView ? 'pt-0' : ''}`}>
                  <Form
                    className={`d-flex flex-column ${styles.Question_Form} ${
                      SideBarView && 'pt-2 gap-2'
                    }`}
                  >
                    {sortedQuestions?.map((subQuestion, qIndex) => {
                      const {
                        input_type: inputType,
                        placeholder,
                        title,
                        options,
                        multiple,
                        childId,
                        id: questionId,
                        position,
                      } = subQuestion || {};

                      const currentQuestionState = questionsState?.[qIndex];
                      const { inputValue, selectedOption } =
                        currentQuestionState || {};

                      const parentPosition = questions.find(
                        (question) => question.id === questionId,
                      ).position;

                      const questionTitle = multiple
                        ? childId
                          ? `${title} ${position - parentPosition + 1}`
                          : `${title} ${1}`
                        : title;

                      const error = errors[questionTitle || placeholder];

                      const questionPosition = questions.findLast(
                        (question) => question.id === questionId,
                      ).position;

                      return (
                        <React.Fragment key={childId || questionId}>
                          <AnswerComponent
                            SideBarView={SideBarView}
                            type={inputType}
                            placeholder={placeholder}
                            inputValue={inputValue}
                            handleInputChange={handleInputChange}
                            title={questionTitle}
                            handleChange={handleChange}
                            selectedOption={selectedOption}
                            options={options}
                            quesIndex={qIndex}
                          />
                          {!!inputValue && error && (
                            <div className="error">{error}</div>
                          )}

                          <div className="d-flex flex-row justify-content-evenly">
                            {questionPosition === position && multiple && (
                              <Button
                                className={styles.add_multiple_btn}
                                variant="outline-primary"
                                size="sm"
                                onClick={() =>
                                  handleMultipleQuestionAdd(questionId, qIndex)
                                }
                              >
                                + Add {title}
                              </Button>
                            )}

                            {childId && (
                              <Button
                                className={styles.remove_multiple_btn}
                                variant="outline-danger"
                                size="sm"
                                onClick={() =>
                                  handleRemoveChildQuestion(qIndex)
                                }
                              >
                                - Remove {title}
                              </Button>
                            )}
                          </div>
                        </React.Fragment>
                      );
                    })}
                    
                    {/* Add Language Dropdown */}
                    <div className={styles.language_dropdown_wrapper}>
                      <label className="Input_label">Content Language</label>
                      <Form.Select
                        value={selectedLanguage}
                        onChange={(e) => handleLanguageChange(e.target.value)}
                        className={styles.language_dropdown}
                      >
                        {LANGUAGE_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </div>

                    {/* Supporting Documents Upload */}
                    {!SideBarView && (
                      <SupportingDocumentsUpload
                        files={supportingFiles}
                        setFiles={handleSupportingFilesChange}
                        extractedContent={supportingContent}
                        isExtracting={isExtracting}
                        extractionProgress={extractionProgress}
                        errors={uploadErrors}
                        setErrors={setUploadErrors}
                      />
                    )}

                    {!SideBarView && (
                      <div className={styles.button_group}>
                        <Button
                          className={styles.save_favorites_btn}
                          variant="outline-secondary"
                          onClick={() => {
                            // TODO: Implement save to favorites functionality
                            console.log('Save to favorites clicked');
                          }}
                        >
                          Save to favorites
                        </Button>
                        <BSButton
                          classList={`${styles.run_prompt_btn} secondary_btn`}
                          variant="dark"
                          disabled={!isSubmitButtonEnabled}
                          onClick={() => handleBlockSave()}
                          ButtonText="Run Prompt"
                        />
                      </div>
                    )}

                    {SideBarView && (
                      <BSButton
                        classList={`${styles.FormContinueBtn} ${styles.FormContinueBtn_sm}`}
                        BtnWrapperClass="d-flex justify-content-end"
                        variant="dark"
                        disabled={!isSubmitButtonEnabled}
                        onClick={() => handleBlockSave()}
                        icon={<FiRefreshCw />}
                        iconPosition="left"
                        ButtonText="Regenerate"
                      />
                    )}
                  </Form>
                </div>
              </div>
            </section>
          </div>
        </section>
        </div>

        <ContentGenerationModal
          onHide={() => {
            setOpenModal(false);
            setStreamContent('');
            setContentGenerated(false);
            abortController?.current.abort();
          }}
          show={openModal}
          string={streamContent}
          contentGenerated={contentGenerated}
          language={selectedLanguage} // Pass language to modal
        />
        </>
      )}
    </>
  );
};

export default Questionnaire;